import { z } from 'zod';
import { noyauCodeSchema } from './noyau.js';

/**
 * The quality control of a translation (#10, the "Texte traduit" tab): what the
 * `rose_task_v5_controle` sub-task of Tahiti says about the text of each noyau. Everything here
 * is Tahiti's, copied as it is: Rosetta words none of it. A code Tahiti gives without a label
 * keeps its code, its underscores turned into spaces ("cause_inventee" -> "cause inventee").
 */

/** A code of Tahiti and the words the screen shows for it. */
export const codeLibelleSchema = z.object({
  /** As Tahiti writes it: `bloquant`, `a_relire`, `ok`, `cause_inventee`, `presente`... */
  code: z.string().min(1),
  /** Tahiti's label ("Bloquant", "À relire", "OK"), or the code with spaces when it gave none. */
  libelle: z.string().min(1),
});
export type CodeLibelle = z.infer<typeof codeLibelleSchema>;

/** A point the control raises on an indicator: Tahiti's sentence, and the passage it is about. */
export const pointControleSchema = z.object({
  /** `points[].raison`: "cause inventée : Relie l'envie de Kouign amann au voyage...". */
  raison: z.string().min(1),
  /** `points[].passage`: words of the translated text, when the point is about a passage. */
  passage: z.string().min(1).optional(),
});
export type PointControle = z.infer<typeof pointControleSchema>;

/** One indicator of the control, whatever it measures. */
export const indicateurControleSchema = z.object({
  /** Tahiti's numbering ("2.3"): how the screen finds an indicator, never shown. */
  numero: z.string().min(1),
  /** `nom`: "Ajouts", "Longueur"... the title of its card. */
  nom: z.string().min(1),
  statut: codeLibelleSchema,
  /** `resume`: "1 ajout : 1 cause inventée", "36 mots pour 30 à 34 (+6 %)". */
  resume: z.string().min(1).optional(),
  points: z.array(pointControleSchema),
});
export type IndicateurControle = z.infer<typeof indicateurControleSchema>;

/** An addition of the translation (indicator 2.3, `elements[]`). */
export const ajoutControleSchema = z.object({
  type: codeLibelleSchema,
  detail: z.string().min(1).optional(),
  passage: z.string().min(1).optional(),
});
export type AjoutControle = z.infer<typeof ajoutControleSchema>;

/**
 * A change the translation declares itself (`rose_task_v5_traduction`, `result_json.changements[]`,
 * #28): a turn of phrase it rewrote. Not part of the control, and with no status of Tahiti: it
 * counts in no counter. `type` is the code of Tahiti ("restructuration", "explicitation").
 */
export const changementTraductionSchema = z.object({
  type: codeLibelleSchema,
  /** The words of the translated text the change is about. */
  passage: z.string().min(1),
  /** `description`, as Tahiti writes it. */
  description: z.string().min(1).optional(),
});
export type ChangementTraduction = z.infer<typeof changementTraductionSchema>;

/** An affirmation of the text of origin and what became of it (indicator 2.2, `elements[]`). */
export const affirmationControleSchema = z.object({
  /** The affirmation, as the inventory of Tahiti words it. */
  texte: z.string().min(1),
  /** `etat`: "presente", "affaiblie"... */
  etat: codeLibelleSchema,
  detail: z.string().min(1).optional(),
  passage: z.string().min(1).optional(),
});
export type AffirmationControle = z.infer<typeof affirmationControleSchema>;

/** The length of the translation against the range aimed at (indicator 3.1, `noyaux[].longueur`). */
export const longueurControleSchema = z.object({
  nombre: z.number().nonnegative(),
  min: z.number().nonnegative(),
  max: z.number().nonnegative(),
  /** "mots". */
  unite: z.string().min(1),
  /** `ecart_pct`: how far out of the range, in percent. */
  ecartPct: z.number().optional(),
  /** `longueurs.source`: the length of the text of origin, in the same unit. */
  source: z.number().nonnegative().optional(),
});
export type LongueurControle = z.infer<typeof longueurControleSchema>;

/** The layout of the translation (indicator 3.2). */
export const miseEnFormeControleSchema = z.object({
  /** `paragraphes.source` and `paragraphes.produit`. */
  paragraphes: z
    .object({
      origine: z.number().int().nonnegative(),
      traduction: z.number().int().nonnegative(),
    })
    .optional(),
  /** `titre_invente`. */
  titreInvente: z.boolean().optional(),
  /** `markdown_ajoute`. */
  markdownAjoute: z.boolean().optional(),
  /** `residus`, the ones Tahiti wrote as texts. */
  residus: z.array(z.string().min(1)),
});
export type MiseEnFormeControle = z.infer<typeof miseEnFormeControleSchema>;

/** How much of the text of origin the translation copied (indicator 3.3). */
export const reecritureControleSchema = z.object({
  /** `taux_recopie`, in percent. */
  tauxRecopie: z.number().min(0).max(100),
  /** `seuil`, in percent: the rate above which the translation does not rewrite enough. */
  seuil: z.number().min(0).max(100).optional(),
});
export type ReecritureControle = z.infer<typeof reecritureControleSchema>;

/** How many indicators have a status (`compte_statuts`). */
export const compteStatutSchema = z.object({
  statut: codeLibelleSchema,
  nombre: z.number().int().nonnegative(),
});
export type CompteStatut = z.infer<typeof compteStatutSchema>;

/**
 * The control of the text of one noyau (an entry of `noyaux[]`). Each part is read on its own:
 * one that is missing or cannot be read is `null` (or left out of `indicateurs`), and the screen
 * shows nothing for it.
 */
export const controleNoyauSchema = z.object({
  noyau: noyauCodeSchema,
  /** `compte_statuts`: "Bloquant", "À relire" and "OK" first, then any other that is not zero. */
  comptes: z.array(compteStatutSchema).nullable(),
  /** Every indicator Tahiti gave, in its order. */
  indicateurs: z.array(indicateurControleSchema),
  ajouts: z.array(ajoutControleSchema).nullable(),
  affirmations: z.array(affirmationControleSchema).nullable(),
  longueur: longueurControleSchema.nullable(),
  miseEnForme: miseEnFormeControleSchema.nullable(),
  reecriture: reecritureControleSchema.nullable(),
  /**
   * The changes the translation of this noyau declares (#28): read in its translation sub-task,
   * not in the control. Absent or `null` when there is none to read.
   */
  changements: z.array(changementTraductionSchema).nullable().optional(),
});
export type ControleNoyau = z.infer<typeof controleNoyauSchema>;

/**
 * The control of a text: one entry per noyau Tahiti controlled, whichever launch of the document
 * translated it (#34: a control is one sub-task per launch, a text may have several launches).
 */
export const controleTraductionSchema = z.object({
  noyaux: z.array(controleNoyauSchema),
  /**
   * The noyaux with no entry above whose launch could not be read (network, timeout, service
   * error): a control that may exist, unlike the absence of one. The screen offers to try again
   * for them. Absent when every launch was read.
   */
  noyauxNonLus: z.array(noyauCodeSchema).optional(),
});
export type ControleTraduction = z.infer<typeof controleTraductionSchema>;
